import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
/**
 * Dialog Component (Layer 1 Primitive)
 *
 * An accessible modal dialog built on Radix UI primitives.
 * Supports focus trapping, scroll locking, and keyboard interactions.
 *
 * @example
 * ```tsx
 * <Dialog open={open} onOpenChange={setOpen}>
 *   <Dialog.Trigger asChild>
 *     <Button>Open Dialog</Button>
 *   </Dialog.Trigger>
 *   <Dialog.Content>
 *     <Dialog.Header>
 *       <Dialog.Title>Dialog Title</Dialog.Title>
 *       <Dialog.Description>Dialog description goes here</Dialog.Description>
 *     </Dialog.Header>
 *     <div>Dialog content</div>
 *     <Dialog.Footer>
 *       <Button variant="secondary" onClick={() => setOpen(false)}>Cancel</Button>
 *       <Button onClick={handleSave}>Save</Button>
 *     </Dialog.Footer>
 *   </Dialog.Content>
 * </Dialog>
 * ```
 */
const DialogRoot = DialogPrimitive.Root;
const DialogTrigger = DialogPrimitive.Trigger;
const DialogPortal = DialogPrimitive.Portal;
const DialogOverlay = React.forwardRef(({ className = '', ...props }, ref) => (_jsx(DialogPrimitive.Overlay, { ref: ref, className: `fixed inset-0 z-50 bg-uw-surface-overlay backdrop-blur-md animate-in fade-in-0 transition-all duration-200 ease-out ${className}`, ...props })));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;
const DialogContent = React.forwardRef(({ className = '', children, ...props }, ref) => (_jsxs(DialogPortal, { children: [_jsx(DialogOverlay, {}), _jsxs(DialogPrimitive.Content, { ref: ref, className: `fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-6 border border-uw-border-default bg-uw-surface-default p-8 shadow-2xl animate-in fade-in-0 zoom-in-95 rounded-lg transition-all duration-200 ease-out ${className}`, ...props, children: [children, _jsxs(DialogPrimitive.Close, { className: "absolute right-4 top-4 rounded-md opacity-70 ring-offset-uw-surface-default transition-all duration-200 ease-out hover:opacity-100 hover:bg-uw-surface-elevated p-1.5 focus:outline-none focus:ring-2 focus:ring-uw-interactive-primary focus:ring-offset-2 disabled:pointer-events-none", children: [_jsxs("svg", { xmlns: "http://www.w3.org/2000/svg", width: "18", height: "18", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", children: [_jsx("line", { x1: "18", y1: "6", x2: "6", y2: "18" }), _jsx("line", { x1: "6", y1: "6", x2: "18", y2: "18" })] }), _jsx("span", { className: "sr-only", children: "Close" })] })] })] })));
DialogContent.displayName = DialogPrimitive.Content.displayName;
const DialogHeader = ({ className = '', ...props }) => (_jsx("div", { className: `flex flex-col space-y-2 text-center sm:text-left ${className}`, ...props }));
DialogHeader.displayName = 'DialogHeader';
const DialogFooter = ({ className = '', ...props }) => (_jsx("div", { className: `flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-3 pt-4 border-t border-uw-border-muted ${className}`, ...props }));
DialogFooter.displayName = 'DialogFooter';
const DialogTitle = React.forwardRef(({ className = '', ...props }, ref) => (_jsx(DialogPrimitive.Title, { ref: ref, className: `text-lg font-semibold leading-none tracking-tight text-uw-text-primary ${className}`, ...props })));
DialogTitle.displayName = DialogPrimitive.Title.displayName;
const DialogDescription = React.forwardRef(({ className = '', ...props }, ref) => (_jsx(DialogPrimitive.Description, { ref: ref, className: `text-sm text-uw-text-secondary ${className}`, ...props })));
DialogDescription.displayName = DialogPrimitive.Description.displayName;
// Attach subcomponents
export const Dialog = Object.assign(DialogRoot, {
    Trigger: DialogTrigger,
    Content: DialogContent,
    Header: DialogHeader,
    Footer: DialogFooter,
    Title: DialogTitle,
    Description: DialogDescription,
});
export { DialogTrigger, DialogContent, DialogHeader, DialogFooter, DialogTitle, DialogDescription, };
//# sourceMappingURL=Dialog.js.map